// @ts-nocheck

import type { Node } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import type { ScoreboardV2Props } from '../../states/scoreboards';
import theme from '../../styled-components/dark';
import { pixelToEm } from '../../utils/common';
import AnimateOnChange from '../AnimateOnChange';
import FlagIcon from '../FlagIcon';
import ScrollView from '../ScrollView';

// Global
const Wrapper = styled.div`
  width: 100%;
  overflow: hidden;
  color: ${theme.Scoreboards.color};
  box-sizing: border-box;
  position: relative;
  font-weight: ${({ $lightFont }) => ($lightFont ? 'normal' : 'bold')};
  font-size: 1.05em;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
`;

const Columnn = styled.div`
  min-width: ${pixelToEm(18)}em;
`;

// Competitors
const Competitors = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex-shrink: 1;
`;

const Competitor = styled.div`
  display: flex;
  height: 50%;
  min-height: ${pixelToEm(18)}em;
  align-items: center;
  min-width: 0; // Needed to make ellipsis work
  box-sizing: border-box;
`;

const StyledFlagIcon = styled(FlagIcon)`
  height: 1.1em;
  width: 1.1em;
  margin-right: 0.3em;
  flex-shrink: 0;
`;

const CompetitorsWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 100%;
`;

const CompetitorEllipsisContainer = styled.div`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const ServerHeightWrapper = styled.span``;

const ServerIcon = styled.div`
  min-width: ${pixelToEm(5)}em;
  width: ${pixelToEm(5)}em;
  height: ${pixelToEm(5)}em;
  background: ${theme.Scoreboards.serverIconBackground};
  border-radius: 50%;
  margin-left: ${pixelToEm(5)}em;
`;

// SetScores
const ScoresArea = styled.div`
  display: flex;
  flex-direction: ${({ $isOverflowing }) => ($isOverflowing ? 'row-reverse' : 'row')};
  flex: 1 0 auto;

  overflow: hidden;
  position: relative;
`;

const SetScoreArea = styled.div`
  z-index: 2;
  display: flex;
  position: relative;
  white-space: nowrap;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  transform-style: preserve-3d;
`;

const SetScoreColumn = styled(Columnn)`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  color: ${theme.Scoreboards.setScoreColumn};

  ${({ $disableHighlightLastSet }) =>
    !$disableHighlightLastSet
      ? css`
          &:last-of-type {
            color: ${theme.Scoreboards.lastSetScoreColumn};
          }
        `
      : ''}
`;

const FlexOverflowWrapper = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  justify-content: space-between;
  padding-right: 0.4em;
`;

// GameScores
const GameScoreColumnWrapper = styled.div``;

const GameScoreColumn = styled(Columnn)`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding-left: 0.4em;
  box-sizing: border-box;
  min-width: ${pixelToEm(32)}em;

  ${({ $border, $light }) =>
    $border
      ? css`
          border-left: ${$light ? 1 : 2}px solid rgba(255, 255, 255, 0.2);
        `
      : ''};
`;

const GameScoreWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-self: center;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
`;

// Correspond au score "en cours" (tout à droite)
const GameScoreRow = styled.div`
  display: flex;
  flex-direction: row;
  min-height: ${pixelToEm(18)}em;
  width: 100%;
  justify-content: center;
  z-index: 2;
  color: ${theme.Scoreboards.gameScoreRow};
`;

const ScoreHeightWrapper = styled.span`
  font-size: ${pixelToEm(15)}em;
  width: 100%;
`;

const Score = styled.div`
  min-height: ${pixelToEm(18)}em;
  opacity: ${({ $fullOpacity }) => ($fullOpacity ? 1 : 0.6)};
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const ScoresWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  height: 100%;
`;

const Wrap = styled.div`
  display: inline-block;
  position: relative;
  z-index: 3;
  height: 100%;
`;

const WrappedScrollView = styled(ScrollView)`
  width: 100%;
  display: flex;
  flex-direction: ${({ $isOverflowing }) => ($isOverflowing ? 'row' : 'row-reverse')};
`;

// Background positionné derrière le score en cours
const AbsoluteBackground = styled.div`
  position: absolute;
  top: 0%;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${theme.Scoreboards.absoluteGreyBackground};
  border-radius: 6px;
`;

const ScoreboardRowSetsLayout = ({
  competitors,
  scores,
  defaultVisibleSetsNumber,
  isBackgroundHighlighted = false, // `isBackgroundHighlighted` is used to display a yellow background for BASKETBALL environment
  disableHighlightLastSet = false,
  isLive,
  light,
}: ScoreboardV2Props): Node => {
  const { secondaries, main } = scores;
  const { score: mainScore } = main ?? {};

  const [isOverflowing, setIsOverflowing] = useState(false);

  const scrollToLastSetScoreRef = useRef();
  const scrollViewWrapperRef = useRef();
  const setScoreWrapperRef = useRef();

  // we filter the score to remove null, empty and undefined values
  // `hasScores` is used to avoid the display of an empty background when we do not have scores
  const hasScores = scores?.main?.score?.filter((score) => score !== null && score !== '').length > 0;

  const isScoreWon = useCallback(
    (index, scoreIndex, setScore) =>
      (!disableHighlightLastSet && index === secondaries.length - 1 && isLive) ||
      setScore.score.every((pScore, i) => scoreIndex === i || parseInt(setScore.score[scoreIndex]) > parseInt(pScore)),
    [isLive, secondaries, disableHighlightLastSet],
  );

  useEffect(() => {
    const setScoreWidth = setScoreWrapperRef?.current?.getBoundingClientRect().width;
    const scrollViewWidth = scrollViewWrapperRef?.current?.getBoundingClientRect().width;

    if (setScoreWidth && scrollViewWidth) {
      if (setScoreWidth >= scrollViewWidth) {
        setIsOverflowing(true);
      }
    }
  }, []);

  return (
    <Wrapper $lightFont={light}>
      <ContentWrapper>
        <FlexOverflowWrapper>
          <Competitors>
            <CompetitorsWrapper>
              {competitors &&
                competitors.map((competitor, index) => (
                  <Competitor key={index}>
                    {competitor.flag ? <StyledFlagIcon flagId={competitor.flag} /> : null}
                    <CompetitorEllipsisContainer>{competitor.name}</CompetitorEllipsisContainer>
                    {competitor.isServing && (
                      <ServerHeightWrapper>
                        <ServerIcon />
                      </ServerHeightWrapper>
                    )}
                  </Competitor>
                ))}
            </CompetitorsWrapper>
          </Competitors>
          <ScoresArea $isOverflowing={isOverflowing}>
            <WrappedScrollView
              defaultVisibleSetsNumber={defaultVisibleSetsNumber}
              vertical={false}
              shouldScrollToElement={secondaries?.length}
              elementToScrollTo={scrollToLastSetScoreRef}
              shouldDisplayScrollbar={false}
              ref={scrollViewWrapperRef}
              isOverflowing={isOverflowing}
            >
              <Wrap ref={setScoreWrapperRef} defaultVisibleSetsNumber={defaultVisibleSetsNumber}>
                <SetScoreArea isInfiniteSets={secondaries?.length > 5}>
                  {secondaries?.map((setScore, index) => (
                    <SetScoreColumn
                      key={index}
                      ref={scrollToLastSetScoreRef}
                      $disableHighlightLastSet={disableHighlightLastSet}
                    >
                      <ScoresWrapper>
                        {setScore?.score?.map((score, scoreIndex) => (
                          <React.Fragment key={scoreIndex}>
                            <Score
                              isInfiniteSets={secondaries?.length > 5}
                              $fullOpacity={isScoreWon(index, scoreIndex, setScore)}
                            >
                              {score}
                            </Score>
                          </React.Fragment>
                        ))}
                      </ScoresWrapper>
                    </SetScoreColumn>
                  ))}
                </SetScoreArea>
              </Wrap>
            </WrappedScrollView>
          </ScoresArea>
        </FlexOverflowWrapper>
        <GameScoreColumnWrapper>
          <GameScoreColumn $border={!!secondaries?.length} $light={light}>
            <GameScoreWrapper>
              {hasScores ? <AbsoluteBackground /> : null}
              {mainScore?.map((gameScore, index) => (
                <GameScoreRow key={index} isBackgroundHighlighted={isBackgroundHighlighted}>
                  <ScoreHeightWrapper>
                    <Score $fullOpacity>
                      <AnimateOnChange>{gameScore}</AnimateOnChange>
                    </Score>
                  </ScoreHeightWrapper>
                </GameScoreRow>
              ))}
            </GameScoreWrapper>
          </GameScoreColumn>
        </GameScoreColumnWrapper>
      </ContentWrapper>
    </Wrapper>
  );
};

export default ScoreboardRowSetsLayout;
